import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { api } from '../../services/api';
import { Badge } from '../shared/Badge';
import { Button } from '../shared/Button';

/**
 * ImageGallery Component
 *
 * Displays archived images available for reuse with performance metadata.
 * Used in the "Reuse Existing Content" tab of Create Stories page.
 *
 * Features:
 * - Grid view of reusable images (posted/expired/archived)
 * - Reuse metadata: usage count, last posted, avg views
 * - Filtering by sentiment
 * - Sorting by recent/performance/least_used
 * - Selection mode for batch reuse
 * - Empty states
 *
 * @example
 * <ImageGallery
 *   accountId="account-123"
 *   onImageSelect={(image) => handleReuse(image)}
 * />
 */
export function ImageGallery({
  accountId,
  onImageSelect,
  selectionMode = 'single',
  maxSelection = 1,
  className = '',
}) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);

  // Filter and sort options
  const [sentiment, setSentiment] = useState(null);
  const [sortBy, setSortBy] = useState('recent');

  // Load archived images
  useEffect(() => {
    if (!accountId) {
      console.warn('[ImageGallery] No accountId provided, skipping image load');
      return;
    }

    const loadImages = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = {
          instagram_account_id: accountId,
          sort: sortBy,
          limit: 50,
        };

        if (sentiment) {
          params.sentiment = sentiment;
        }

        console.log('[ImageGallery] Fetching archived images with params:', params);
        const data = await api.getArchivedImages(params);
        console.log('[ImageGallery] Received response:', { count: data?.count, imageCount: data?.images?.length });

        setImages(data.images || []);
      } catch (err) {
        console.error('[ImageGallery] Failed to load archived images:', err);
        setError(err.message || 'Failed to load images');
      } finally {
        setLoading(false);
      }
    };

    loadImages();
  }, [accountId, sentiment, sortBy]);

  const handleImageClick = (image) => {
    if (selectionMode === 'single') {
      onImageSelect(image);
    } else {
      // Multi-select mode
      const isSelected = selectedImages.some((img) => img.image_id === image.image_id);

      if (isSelected) {
        setSelectedImages(selectedImages.filter((img) => img.image_id !== image.image_id));
      } else {
        if (selectedImages.length >= maxSelection) {
          // Max selection reached
          return;
        }
        setSelectedImages([...selectedImages, image]);
      }
    }
  };

  const handleBulkReuse = () => {
    if (selectedImages.length === 0) return;
    onImageSelect(selectedImages);
    setSelectedImages([]);
  };

  const isSelected = (image) => {
    return selectedImages.some((img) => img.image_id === image.image_id);
  };

  // Format usage count
  const formatUsageCount = (count) => {
    if (count === 1) return '1 time';
    return `${count} times`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (e) {
      return 'Unknown';
    }
  };

  // Format number with K/M suffix
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Empty state (only show when NOT loading and NO error)
  if (!loading && !error && images.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="rounded-full bg-gray-100 p-4 mb-4">
          <svg
            className="w-12 h-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          {sentiment ? 'No images match your filters' : 'No images available yet'}
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          {sentiment
            ? 'Try adjusting your filters or upload new content'
            : 'Upload and post your first images to start reusing them'}
        </p>
        {sentiment && (
          <Button variant="secondary" onClick={() => setSentiment(null)}>
            Clear Filters
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`} role="region" aria-label="Image gallery with reuse options">
      {/* Filters & Sort */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between" role="toolbar" aria-label="Gallery filters and sorting">
        {/* Sentiment Filter */}
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={sentiment === null ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setSentiment(null)}
          >
            All
          </Button>
          <Button
            variant={sentiment === 'positive' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setSentiment('positive')}
          >
            Positive
          </Button>
          <Button
            variant={sentiment === 'neutral' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setSentiment('neutral')}
          >
            Neutral
          </Button>
          <Button
            variant={sentiment === 'negative' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setSentiment('negative')}
          >
            Negative
          </Button>
        </div>

        {/* Sort Options */}
        <div className="flex items-center gap-2">
          <label htmlFor="sort-select" className="text-sm text-gray-600">Sort by:</label>
          <select
            id="sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-purple-500"
            aria-label="Sort images by"
          >
            <option value="recent">Most Recent</option>
            <option value="performance">Best Performing</option>
            <option value="least_used">Least Used</option>
          </select>
        </div>
      </div>

      {/* Selection Toolbar (Multi-select mode) */}
      {selectionMode === 'multi' && selectedImages.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center justify-between" role="status" aria-live="polite">
          <span className="text-sm font-medium text-purple-900">
            {selectedImages.length} image{selectedImages.length !== 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => setSelectedImages([])}>
              Clear
            </Button>
            <Button variant="primary" size="sm" onClick={handleBulkReuse}>
              Generate Captions
            </Button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="aspect-square bg-gray-200 rounded-lg animate-pulse" />
          ))}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-sm text-red-600">{error}</p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => window.location.reload()}
            className="mt-2"
          >
            Retry
          </Button>
        </div>
      )}

      {/* Image Grid */}
      {!loading && !error && (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
          role="grid"
          aria-label={`Image gallery showing ${images.length} image${images.length !== 1 ? 's' : ''}`}
        >
          {images.map((image) => (
            <ImageCard
              key={image.image_id}
              image={image}
              isSelected={isSelected(image)}
              onClick={() => handleImageClick(image)}
              formatUsageCount={formatUsageCount}
              formatDate={formatDate}
              formatNumber={formatNumber}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * LazyImage Component (Internal)
 *
 * Lazy-loads images using Intersection Observer API
 * Only loads image when it enters the viewport
 */
function LazyImage({ src, alt, className }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const currentRef = imgRef.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before image enters viewport
      }
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div ref={imgRef} className={className}>
      {isInView && (
        <>
          {/* Placeholder while loading */}
          {!isLoaded && (
            <div className="absolute inset-0 bg-gray-200 animate-pulse" />
          )}
          <img
            src={src}
            alt={alt}
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setIsLoaded(true)}
            loading="lazy"
            role="presentation"
            aria-hidden="true"
          />
        </>
      )}
      {/* Placeholder before in view */}
      {!isInView && <div className="absolute inset-0 bg-gray-200" />}
    </div>
  );
}

LazyImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
};

/**
 * ImageCard Component (Internal)
 *
 * Individual image card with reuse metadata
 */
function ImageCard({ image, isSelected, onClick, formatUsageCount, formatDate, formatNumber }) {
  const metadata = image.reuse_metadata || {};

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  const ariaLabel = `Image with ${image.sentiment} sentiment. Used ${formatUsageCount(metadata.usage_count || 0)}, last posted ${formatDate(metadata.last_posted_at)}${metadata.avg_views > 0 ? `, average views ${formatNumber(metadata.avg_views)}` : ''}. ${isSelected ? 'Selected' : 'Not selected'}. Press Enter or Space to ${isSelected ? 'deselect' : 'select'}.`;

  return (
    <div
      role="gridcell"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      aria-label={ariaLabel}
      aria-selected={isSelected}
      className={`
        relative group cursor-pointer rounded-lg overflow-hidden
        border-2 transition-all duration-200
        focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
        ${isSelected ? 'border-purple-500 ring-2 ring-purple-200' : 'border-transparent hover:border-purple-300'}
      `}
    >
      {/* Image */}
      <div className="aspect-square relative bg-gray-100">
        <LazyImage
          src={image.sas_url}
          alt={image.content_description || 'Image'}
          className="w-full h-full"
        />

        {/* Selection Checkbox */}
        {isSelected && (
          <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        )}

        {/* Sentiment Badge */}
        <div className="absolute top-2 right-2">
          <Badge variant={image.sentiment === 'positive' ? 'green' : image.sentiment === 'negative' ? 'red' : 'gray'}>
            {image.sentiment}
          </Badge>
        </div>

        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
          <span className="text-white font-medium opacity-0 group-hover:opacity-100 transition-opacity">
            Select
          </span>
        </div>
      </div>

      {/* Metadata */}
      <div className="p-3 bg-white space-y-2">
        {/* Usage Count */}
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600">Used:</span>
          <span className="font-medium text-gray-900">
            {formatUsageCount(metadata.usage_count || 0)}
          </span>
        </div>

        {/* Last Posted */}
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600">Posted:</span>
          <span className="font-medium text-gray-900">
            {formatDate(metadata.last_posted_at)}
          </span>
        </div>

        {/* Avg Views */}
        {metadata.avg_views > 0 && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">Avg Views:</span>
            <span className="font-medium text-purple-600">
              {formatNumber(metadata.avg_views)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

ImageGallery.propTypes = {
  accountId: PropTypes.string.isRequired,
  onImageSelect: PropTypes.func.isRequired,
  selectionMode: PropTypes.oneOf(['single', 'multi']),
  maxSelection: PropTypes.number,
  className: PropTypes.string,
};

ImageCard.propTypes = {
  image: PropTypes.object.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  formatUsageCount: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired,
  formatNumber: PropTypes.func.isRequired,
};

export default ImageGallery;
